<template>
  <v-container>
    <v-responsive>
      <!-- Confirmation page -->
      <v-card v-if="orderSubmitted" elevation="1" outlined tile>
        <div class="text-center my-10">
          <v-icon size="40" color="success">mdi-check-circle</v-icon>
          <h2 class="my-5">Vielen Dank für Ihre Bestellung!</h2>
          <p>
            Wir haben Ihre Anfrage erhalten und bearbeiten sie so schnell wie
            möglich.
          </p>
        </div>
      </v-card>

      <!-- Stepper flow -->
      <v-stepper v-else v-model="step" class="stepper-background" alt-labels>
        <!-- Stepper Header -->
        <v-stepper-header>
          <v-stepper-item
            v-for="(item, index) in routes"
            :key="index"
            :title="item.name"
            :value="index + 1"
          />
        </v-stepper-header>

        <v-card elevation="1" outlined tile>
          <v-stepper-window v-model="step">
            <!-- Step 1: Geodaten -->
            <v-stepper-window-item :value="1">
              <h2>Geodaten</h2>
              <div class="mb-2 mt-2">
                <p>
                  Benötigen Sie amtliche Pläne, Leitungsdaten oder geografische
                  Informationen für Ihr Bauvorhaben oder Ihre Projektplanung?
                  <br />Über unsere Geodaten-Bestellung können Sie ganz einfach
                  die passenden Pläne und Daten unserer Mandatsgemeinden
                  bestellen.
                </p>
              </div>
              <div v-for="(order, index) in orders" :key="index">
                <OrderForm
                  :ref="`orderForm${index}`"
                  :order="order"
                  :communities="communities"
                  :is-first-order="index === 0"
                  @valid="updateOrderValidity(index, $event)"
                  @removeOrder="removeOrder(index)"
                  @addOrder="addOrder"
                  @update:order="updateOrder(index, $event)"
                  @heightChange="handleHeightChange"
                />
                <v-divider
                  v-if="index < orders.length - 1"
                  class="my-4"
                ></v-divider>
              </div>
            </v-stepper-window-item>

            <!-- Step 2: Bestellangaben -->
            <v-stepper-window-item :value="2">
              <h2>Bestellangaben</h2>
              <OrderDetails
                ref="orderDetailsRef"
                :mainOrder="mainOrder"
                @update:mainOrder="mainOrder = $event"
                @valid="orderDetails = $event"
                @heightChange="handleHeightChange"
              />
            </v-stepper-window-item>

            <!-- Step 3: Übersicht -->
            <v-stepper-window-item :value="3">
              <h2>Übersicht</h2>
              <OrderSummary
                ref="orderSummaryRef"
                :mainOrder="mainOrder"
                :orders="orders"
                :conditionsAccepted="conditionsAccepted"
                :hCaptchaAccepted="hCaptchaAccepted"
                @update:conditionsAccepted="conditionsAccepted = $event"
                @update:hCaptchaAccepted="hCaptchaAccepted = $event"
                @update:hCaptchaToken="hCaptchaToken = $event"
                @heightChange="handleHeightChange"
              />
            </v-stepper-window-item>
          </v-stepper-window>
        </v-card>

        <!-- Navigation Buttons -->
        <v-container class="v-stepper-actions">
          <v-btn
            :disabled="step === 1 || isSubmitting"
            @click="step--"
            color="secondary"
          >
            Zurück
          </v-btn>
          <v-btn
            :disabled="!isStepValid || isSubmitting"
            @click="handleNext()"
            color="primary"
          >
            <v-progress-circular
              v-if="isSubmitting"
              indeterminate
              color="white"
              size="20"
              class="mr-2"
            />
            {{
              isSubmitting
                ? 'Wird gesendet...'
                : step === routes.length
                  ? 'Kostenpflichtig bestellen'
                  : 'Weiter'
            }}
          </v-btn>
        </v-container>
      </v-stepper>
    </v-responsive>
  </v-container>
</template>

<script>
import axios from 'axios'
import OrderForm from './OrderForm.vue'
import OrderDetails from './OrderDetails.vue'
import OrderSummary from './OrderSummary.vue'
import { nextTick } from 'vue'

export default {
  name: 'OrderCreate',
  components: {
    OrderForm,
    OrderDetails,
    OrderSummary,
  },
  data() {
    return {
      step: 1,
      isSubmitting: false,
      orderSubmitted: false,
      routes: [
        { name: 'Geodaten', route: '/order' },
        { name: 'Bestellangaben', route: '/order' },
        { name: 'Übersicht', route: '/order' },
      ],
      communities: [],
      orders: [
        {
          community: null,
          files: [],
          amount: 0,
        },
      ],
      conditionsAccepted: false,
      hCaptchaAccepted: false,
      hCaptchaToken: null,
      orderValidity: [false],
      orderDetails: false,
      mainOrder: {
        companyName: '',
        name: '',
        addressAddition: '',
        address: '',
        postcode: '',
        place: '',
        phoneNumber: '',
        mail: '',
        comments: '',
      },
      resizeObserver: null,
      childHeights: [],
    }
  },
  computed: {
    isStepValid() {
      if (this.step === 1) return this.orderValidity.every((v) => v)
      if (this.step === 2) return this.orderDetails
      if (this.step === 3)
        return this.conditionsAccepted && this.hCaptchaAccepted
      return true
    },
  },
  watch: {
    step(newStep, oldStep) {
      if (newStep !== oldStep) {
        nextTick(() => {
          this.requestHeightUpdate()
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }, 50)
        })
      }
    },
  },
  mounted() {
    this.getCommunities()
    this.setupResizeObserver()
    window.addEventListener('message', this.handleParentMessage)
  },
  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
    window.removeEventListener('message', this.handleParentMessage)
  },
  methods: {
    handleHeightChange(childHeight) {
      this.sendHeightToParent(childHeight)
    },
    sendHeightToParent(childHeight) {
      const height = childHeight || (this.$el ? this.$el.scrollHeight : 0)
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'iframeResize', height }, '*')
      }
    },
    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        this.sendHeightToParent()
      })
      this.resizeObserver.observe(this.$el)
    },
    handleParentMessage(event) {
      if (event.data?.type === 'requestHeight') {
        this.sendHeightToParent()
      }
    },
    requestHeightUpdate() {
      nextTick(() => {
        if (this.step === 1) {
          this.orders.forEach((_, index) => {
            const ref = this.$refs[`orderForm${index}`]
            if (ref && ref[0]?.sendHeightUpdate) ref[0].sendHeightUpdate()
          })
        } else if (this.step === 2) {
          const ref = this.$refs.orderDetailsRef
          if (ref?.sendHeightUpdate) ref.sendHeightUpdate()
        } else if (this.step === 3) {
          const ref = this.$refs.orderSummaryRef
          if (ref?.sendHeightUpdate) ref.sendHeightUpdate()
        }
      })
    },
    handleNext() {
      if (this.step === this.routes.length) {
        this.submitOrder()
      } else {
        this.step++
        nextTick(() => this.requestHeightUpdate())
      }
    },
    updateOrder(index, updatedOrder) {
      this.orders.splice(index, 1, updatedOrder)
      if (this.step === 1) nextTick(this.requestHeightUpdate)
    },
    sortCommunities(communities){
      return communities.sort((a, b) => a.name.localeCompare(b.name));
    },
    async getCommunities() {
      try {
        const result = await axios.get(
          `${process.env.VUE_APP_ZKL_BACKEND_URL}/v1/services/municipality`,
        )
        this.communities = this.sortCommunities(result.data)
        nextTick(this.requestHeightUpdate)
      } catch (error) {
        console.error('Error fetching communities:', error)
      }
    },
    addOrder() {
      this.orders.push({
        community: '',
        orderAddress: '',
        orderParcel: '',
        files: [],
        amount: 0,
      })
      this.orderValidity.push(false)
      if (this.step === 1) nextTick(this.requestHeightUpdate)
    },
    removeOrder(index) {
      if (this.orders.length > 1) {
        this.orders.splice(index, 1)
        this.orderValidity.splice(index, 1)
        if (this.step === 1) nextTick(this.requestHeightUpdate)
      }
    },
    updateOrderValidity(index, isValid) {
      this.orderValidity[index] = isValid
    },
    calculateTotalOrderAmount() {
      return this.orders.reduce((sum, o) => sum + (o.amount || 0), 0)
    },
    roundToNearestFiveRappen(amount) {
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
      return Math.round(numAmount * 20) / 20
    },
    async submitOrder() {
      this.isSubmitting = true
      try {
        const totalOrderAmount = this.calculateTotalOrderAmount()
        const vatRate = 0.081
        const vatAmount = this.roundToNearestFiveRappen(
          totalOrderAmount * vatRate,
        )
        const totalAmountWithVAT = this.roundToNearestFiveRappen(
          totalOrderAmount + vatAmount,
        )
        const orderData = {
          orders: this.orders.map((o) => ({
            ...o,
            community: o.community?.name || null,
            selectedProducts: o.selectedProducts || [],
          })),
          mainOrder: this.mainOrder,
          totalOrderAmount,
          vatAmount,
          totalAmountWithVAT,
        }
        const formData = new FormData()
        formData.append('orderData', JSON.stringify(orderData))
        formData.append('hCaptchaToken', this.hCaptchaToken)
        this.orders.forEach((order, index) => {
          order.files?.forEach((file) => {
            formData.append(`files_order_${index}`, file)
          })
        })
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/order`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        )
        this.orderSubmitted = true
        this.sendHeightToParent()
      } catch (error) {
        console.error('Error submitting order:', error)
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.v-stepper-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}
</style>
